import React, { useEffect, useRef } from 'react';
import { CameraIcon, PhotoIcon, DocumentIcon } from '@heroicons/react/24/outline';

// Bottom-sheet picker shown on Android (which lacks the iOS WKWebView's native
// action sheet for <input type="file" accept="image/*">). Lets the user choose
// between camera, photo library, or a general file picker. Each option fires a
// hidden <input> with the right accept + capture combo and returns File[] via
// onPick.
//
// On iOS the WebView already presents the native sheet, so callers should
// gate this component behind Capacitor.getPlatform() === 'android'.

type SheetSource = 'camera' | 'library' | 'file';

export interface UploadActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onPick: (files: File[], source: SheetSource) => void;
  /** Accept string used when the user picks "Choose File". Pass an empty
   *  string or omit to hide the Choose File option (e.g. image-only contexts). */
  fileAccept?: string;
  /** Override the default accept for the camera + library paths if you want to
   *  restrict further. Defaults to "image/*". */
  imageAccept?: string;
  title?: string;
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  zIndex: 50,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const SHEET_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: 500,
  background: '#FFFFFF',
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  paddingTop: 12,
  paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
  boxShadow: '0 -4px 24px rgba(57, 38, 79, 0.2)',
};

const GRABBER_STYLE: React.CSSProperties = {
  width: 36,
  height: 4,
  borderRadius: 2,
  background: '#DDD8E4',
  margin: '0 auto 8px',
};

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'Grift, sans-serif',
  fontWeight: 700,
  fontSize: 16,
  color: '#39264F',
  textAlign: 'center',
  padding: '8px 16px 12px',
};

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 24px',
  background: 'transparent',
  border: 0,
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'Grift, sans-serif',
  fontWeight: 500,
  fontSize: 16,
  color: '#39264F',
};

const CANCEL_STYLE: React.CSSProperties = {
  ...ROW_STYLE,
  justifyContent: 'center',
  fontWeight: 600,
  color: '#573A78',
  borderTop: '1px solid #EBE5F0',
  marginTop: 4,
};

export const UploadActionSheet: React.FC<UploadActionSheetProps> = ({
  isOpen,
  onClose,
  onPick,
  fileAccept,
  imageAccept = 'image/*',
  title = 'Add an image',
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Esc key dismiss for desktop debug ergonomics
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    source: SheetSource,
  ) => {
    const { target } = e;
    const list = target.files ? Array.from(target.files) : [];
    // Clear so picking the same file again still fires onChange next time.
    target.value = '';
    if (list.length > 0) {
      onPick(list, source);
    }
    onClose();
  };

  const triggerInput = (source: SheetSource) => {
    if (source === 'camera') cameraInputRef.current?.click();
    else if (source === 'library') libraryInputRef.current?.click();
    else fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div role="dialog" aria-modal="true" style={OVERLAY_STYLE}>
      {/* Backdrop is a real button so screen-reader + keyboard users can dismiss */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 0,
          cursor: 'default',
        }}
      />
      <div style={{ ...SHEET_STYLE, position: 'relative', zIndex: 1 }}>
        <div style={GRABBER_STYLE} />
        <div style={TITLE_STYLE}>{title}</div>

        <button type="button" style={ROW_STYLE} onClick={() => triggerInput('camera')} data-cy="upload-sheet-camera">
          <CameraIcon className="h-6 w-6" style={{ color: '#573A78' }} />
          Take Photo
        </button>

        <button type="button" style={ROW_STYLE} onClick={() => triggerInput('library')} data-cy="upload-sheet-library">
          <PhotoIcon className="h-6 w-6" style={{ color: '#573A78' }} />
          Photo Library
        </button>

        {fileAccept ? (
          <button type="button" style={ROW_STYLE} onClick={() => triggerInput('file')} data-cy="upload-sheet-file">
            <DocumentIcon className="h-6 w-6" style={{ color: '#573A78' }} />
            Choose File
          </button>
        ) : null}

        <button type="button" style={CANCEL_STYLE} onClick={onClose} data-cy="upload-sheet-cancel">
          Cancel
        </button>
      </div>

      {/* Hidden inputs — each scoped to its own source so accept/capture stay isolated */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={imageAccept}
        capture="environment"
        className="hidden"
        onChange={(e) => handleInputChange(e, 'camera')}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept={imageAccept}
        className="hidden"
        onChange={(e) => handleInputChange(e, 'library')}
      />
      {fileAccept ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={fileAccept}
          className="hidden"
          onChange={(e) => handleInputChange(e, 'file')}
        />
      ) : null}
    </div>
  );
};

export default UploadActionSheet;
